import { PlusOutlined } from '@ant-design/icons'
import { Breadcrumb, Button, Select, Space, Spin, Table, Tabs } from 'antd'
import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useClusters } from '@/hooks/useClusters'
import { useUserGroups } from '@/hooks/useUserGroups'
import { useVmConfigurations } from '@/hooks/useVmConfigurations'

import type { PriceResourceType } from '../user-groups/components/AddPriceModal'
import { AddPriceModal } from '../user-groups/components/AddPriceModal'
import {
  clusterColumn,
  formatVmConfig,
  pricePerHourColumn,
  pricePerMonthColumn,
} from '../user-groups/components/priceColumns'
import type { PriceGroup } from '../user-groups/components/useGroupPrices'
import { useGroupPrices } from '../user-groups/components/useGroupPrices'

const EMPTY = { emptyText: 'No prices found' }
const SCROLL = { x: 800 }

function PriceGroupTable({
  groups,
  resourceType,
}: {
  groups: PriceGroup[]
  resourceType: PriceResourceType
}) {
  const navigate = useNavigate()

  return (
    <Table<PriceGroup>
      dataSource={groups}
      rowKey={(r) => `${r.userGroup}:${r.key}`}
      pagination={false}
      size="small"
      scroll={SCROLL}
      locale={EMPTY}
      onRow={(record) => ({
        style: { cursor: 'pointer' },
        onClick: () =>
          navigate(
            `/user-groups/${record.userGroup}/prices/${resourceType}/${encodeURIComponent(record.key)}`,
          ),
      })}
      columns={[
        {
          title: 'Group',
          dataIndex: 'userGroup',
          width: 150,
          sorter: (a, b) => a.userGroup.localeCompare(b.userGroup),
        },
        clusterColumn((r) => r.clusterId),
        {
          title: 'Resource',
          dataIndex: 'resourceLabel',
          width: 220,
          sorter: (a, b) => a.resourceLabel.localeCompare(b.resourceLabel),
        },
        pricePerHourColumn((r) => r.currentPrice?.pricePerHourPerQty ?? '0'),
        pricePerMonthColumn((r) => r.currentPrice?.pricePerHourPerQty ?? '0'),
      ]}
    />
  )
}

export default function PricesListPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [addModal, setAddModal] = useState<PriceResourceType | null>(null)

  const activeResource = (searchParams.get('tab') as PriceResourceType) || 'vm'
  const groupFilter = searchParams.get('group') || undefined
  const clusterId = searchParams.get('cluster') || undefined
  const configFilter = searchParams.get('config') || undefined

  type FilterKey = 'tab' | 'group' | 'cluster' | 'config'
  const setFilter = (key: FilterKey, value: string | undefined) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (value) {
          next.set(key, value)
        } else {
          next.delete(key)
        }
        return next
      },
      { replace: true },
    )
  }

  const { vmGroups, storageGroups, ipGroups, loading, refetchAll } =
    useGroupPrices(groupFilter, clusterId)
  const clusters = useClusters()
  const { configurations } = useVmConfigurations()
  const userGroups = useUserGroups()

  const filteredVm = useMemo(
    () =>
      configFilter
        ? vmGroups.filter((g) => g.key.endsWith(`:${configFilter}`))
        : vmGroups,
    [vmGroups, configFilter],
  )

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }} items={[{ title: 'Prices' }]} />
      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          placeholder="All groups"
          allowClear
          style={{ width: 200 }}
          value={groupFilter}
          onChange={(v) => setFilter('group', v)}
        >
          {userGroups.map((g) => (
            <Select.Option key={g.name} value={g.name}>
              {g.name}
            </Select.Option>
          ))}
        </Select>
        <Select
          placeholder="All clusters"
          allowClear
          style={{ width: 250 }}
          value={clusterId}
          onChange={(v) => setFilter('cluster', v)}
        >
          {clusters.map((c) => (
            <Select.Option key={c.id} value={c.id}>
              {c.name}
            </Select.Option>
          ))}
        </Select>
        {activeResource === 'vm' && (
          <Select
            placeholder="All configurations"
            allowClear
            style={{ width: 300 }}
            value={configFilter}
            onChange={(v) => setFilter('config', v)}
          >
            {configurations.map((c) => (
              <Select.Option key={c.id} value={c.id}>
                {formatVmConfig(c)}
              </Select.Option>
            ))}
          </Select>
        )}
      </Space>

      <Tabs
        activeKey={activeResource}
        onChange={(k) => setFilter('tab', k)}
        items={[
          {
            key: 'vm',
            label: `VM (${filteredVm.length})`,
            children: <PriceGroupTable groups={filteredVm} resourceType="vm" />,
          },
          {
            key: 'storage',
            label: `Storage (${storageGroups.length})`,
            children: (
              <PriceGroupTable groups={storageGroups} resourceType="storage" />
            ),
          },
          {
            key: 'public-ip',
            label: `Public IP (${ipGroups.length})`,
            children: (
              <PriceGroupTable groups={ipGroups} resourceType="public-ip" />
            ),
          },
        ]}
        tabBarExtraContent={
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setAddModal(activeResource)}
          >
            Add Price
          </Button>
        }
      />

      {addModal && (
        <AddPriceModal
          open
          groups={userGroups}
          prefill={{
            groupName: groupFilter,
            resourceType: addModal,
          }}
          onClose={() => setAddModal(null)}
          onSuccess={() => {
            setAddModal(null)
            refetchAll()
          }}
        />
      )}
    </div>
  )
}
